import { isCurrentHashRoute, redirectToHashRoute } from './hashRoutes';

import { API_BASE_URL } from './apiBase';

interface AdminUser {
  username: string;
}

interface AdminLoginResponse {
  token: string;
  admin: AdminUser;
  expires_in_seconds?: number;
  idle_timeout_seconds?: number;
}

let currentAdmin: AdminUser | null = null;
let currentToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let activityMonitorStarted = false;
let lastActivityAt = Date.now();
let idleTimeoutMs = 15 * 60 * 1000;

function applyAdminAuthResponse(data: AdminLoginResponse): void {
  currentToken = data.token;
  currentAdmin = data.admin;
  if (Number.isFinite(data.idle_timeout_seconds)) {
    idleTimeoutMs = Math.max(60_000, Number(data.idle_timeout_seconds) * 1000);
  }
}

function clearAdminAuthState(): void {
  currentAdmin = null;
  currentToken = null;
}

function redirectToAdminLogin(reason: string): void {
  if (typeof window === 'undefined') return;
  if (isCurrentHashRoute('/login')) return;
  redirectToHashRoute('/login', { reason, expired: '1' });
}

function markActivity(): void {
  lastActivityAt = Date.now();
}

function startActivityMonitor(): void {
  if (activityMonitorStarted || typeof window === 'undefined') return;
  activityMonitorStarted = true;

  const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'focus'];
  for (const eventName of events) {
    window.addEventListener(eventName, markActivity, { passive: true });
  }

  window.setInterval(async () => {
    if (!currentAdmin || !currentToken) return;

    const now = Date.now();
    const idleForMs = now - lastActivityAt;
    if (idleForMs > idleTimeoutMs) {
      await adminLogout({ redirectToLogin: true, reason: 'idle_timeout' });
      return;
    }

    const recentlyActive = idleForMs <= Math.min(120_000, idleTimeoutMs / 2);
    if (recentlyActive) {
      await refreshAdminSession({ silent: true, redirectOnFailure: true });
    }
  }, 60_000);
}

export async function refreshAdminSession(
  options: { silent?: boolean; redirectOnFailure?: boolean } = {},
): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const hadSession = Boolean(currentAdmin && currentToken);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        clearAdminAuthState();
        if (options.redirectOnFailure && hadSession) {
          redirectToAdminLogin('session_expired');
        }
        return false;
      }
      const data = await response.json() as AdminLoginResponse;
      applyAdminAuthResponse(data);
      return true;
    } catch {
      if (!options.silent) {
        clearAdminAuthState();
      }
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function initializeAdminAuth(): Promise<void> {
  startActivityMonitor();
  markActivity();
  await refreshAdminSession({ silent: true });
}

export async function adminLogin(
  username: string,
  password: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        username,
        password,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Login failed' }));
      return { success: false, error: String(error?.error || 'Login failed') };
    }

    const data = await response.json() as AdminLoginResponse;
    applyAdminAuthResponse(data);
    startActivityMonitor();
    markActivity();
    return { success: true };
  } catch (error: any) {
    return { success: false, error: String(error?.message || 'Login failed') };
  }
}

export async function adminLogout(
  options: { redirectToLogin?: boolean; reason?: string } = {},
): Promise<void> {
  return adminLogoutWithOptions(options);
}

async function adminLogoutWithOptions(options: { redirectToLogin?: boolean; reason?: string }): Promise<void> {
  const token = currentToken;
  clearAdminAuthState();
  try {
    await fetch(`${API_BASE_URL}/admin/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: token
        ? { Authorization: `Bearer ${token}` }
        : undefined,
    });
  } catch {
    // best-effort logout
  }
  if (options.redirectToLogin) {
    redirectToAdminLogin(options.reason || 'session_expired');
  }
}

export async function adminLogoutAndRedirect(reason = 'session_expired'): Promise<void> {
  await adminLogoutWithOptions({ redirectToLogin: true, reason });
}

export function getAdminToken(): string | null {
  return currentToken;
}

export function getAdminUser(): AdminUser | null {
  return currentAdmin;
}

export function isAdminAuthenticated(): boolean {
  return Boolean(currentToken && currentAdmin);
}
